import React from 'react';
import { Button, Select } from '@mantine/core';
import { Button as SharedButton } from '../../../../components/Button';
import type { PrintJobView, PrinterView } from '../../../../services/printers/printers.service';
import settingsClasses from './shared/settings-shared.module.scss';

type PosPrintersSettingsContentProps = {
  activePrinters: PrinterView[];
  selectedTestPrinterId: number | null;
  isLoadingPrinters: boolean;
  isSendingPrinterTest: boolean;
  isLoadingPrintJobs: boolean;
  isUpdatingPrintJob: boolean;
  printJobs: PrintJobView[];
  onSelectTestPrinterId: (printerId: number | null) => void;
  onRefreshPrinters: () => void;
  onRefreshPrintJobs: () => void;
  onCreatePrinterTestPrint: () => void;
  onUpdatePrintJobStatus: (
    jobId: number,
    status: 'queued' | 'claimed' | 'printed' | 'failed',
  ) => void;
};

const PosPrintersSettingsContentComponent: React.FC<PosPrintersSettingsContentProps> = ({
  activePrinters,
  selectedTestPrinterId,
  isLoadingPrinters,
  isSendingPrinterTest,
  isLoadingPrintJobs,
  isUpdatingPrintJob,
  printJobs,
  onSelectTestPrinterId,
  onRefreshPrinters,
  onRefreshPrintJobs,
  onCreatePrinterTestPrint,
  onUpdatePrintJobStatus,
}) => {
  return (
    <section className={settingsClasses.section}>
      <p className={settingsClasses.lead}>שלח הדפסת בדיקה דטרמיניסטית של 5 שורות למדפסת פעילה לצורכי אבחון.</p>
      <div className={settingsClasses.formRow}>
        <Select
          data-testid="printers-settings-test-printer"
          label="מדפסת לבדיקה"
          aria-label="Test Printer"
          placeholder={activePrinters.length === 0 ? 'אין מדפסות פעילות' : 'בחר מדפסת'}
          data={activePrinters.map((printer) => ({ value: String(printer.id), label: printer.name }))}
          value={selectedTestPrinterId !== null ? String(selectedTestPrinterId) : null}
          onChange={(value) => onSelectTestPrinterId(value !== null ? Number(value) : null)}
          disabled={isLoadingPrinters || activePrinters.length === 0 || isSendingPrinterTest}
          radius="md"
        />
      </div>
      <div className={settingsClasses.formActions}>
        <Button
          variant="default"
          radius="md"
          data-testid="printers-settings-refresh-printers"
          onClick={onRefreshPrinters}
          disabled={isLoadingPrinters || isSendingPrinterTest || isLoadingPrintJobs}
        >
          {isLoadingPrinters ? 'מרענן...' : 'רענן מדפסות'}
        </Button>
        <Button
          variant="default"
          radius="md"
          data-testid="printers-settings-refresh-jobs"
          onClick={onRefreshPrintJobs}
          disabled={isLoadingPrintJobs || isSendingPrinterTest || isUpdatingPrintJob}
        >
          {isLoadingPrintJobs ? 'מרענן עבודות...' : 'רענן עבודות הדפסה'}
        </Button>
        <SharedButton
          variant="save"
          size="sm"
          dataTestId="printers-settings-send-test"
          onClick={onCreatePrinterTestPrint}
          disabled={
            isSendingPrinterTest
            || isLoadingPrinters
            || activePrinters.length === 0
            || selectedTestPrinterId === null
          }
        >
          {isSendingPrinterTest ? 'שולח...' : 'שלח הדפסת בדיקה'}
        </SharedButton>
      </div>
      <div className={settingsClasses.tableWrap}>
        <table className={settingsClasses.table}>
          <thead>
            <tr>
              <th>עבודה</th>
              <th>מדפסת</th>
              <th>סטטוס</th>
              <th>נוצר</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {printJobs.map((job) => {
              const statusLabelMap: Record<string, string> = {
                queued: 'בתור',
                claimed: 'נאספה',
                printed: 'הודפסה',
                failed: 'נכשלה',
              };
              const statusClass =
                job.status === 'printed' || job.status === 'claimed'
                  ? settingsClasses.statusActive
                  : settingsClasses.statusInactive;
              return (
                <tr key={job.id}>
                  <td>#{job.id}</td>
                  <td>{job.printerName}</td>
                  <td>
                    <span className={statusClass}>{statusLabelMap[job.status] ?? job.status}</span>
                  </td>
                  <td>{new Date(job.createdAt).toLocaleString()}</td>
                  <td>
                    <div className={settingsClasses.actionsCell}>
                      <Button
                        size="xs"
                        variant="default"
                        radius="md"
                        data-testid={`printers-settings-job-claimed-${job.id}`}
                        onClick={() => onUpdatePrintJobStatus(job.id, 'claimed')}
                        disabled={isUpdatingPrintJob || job.status === 'claimed'}
                      >
                        סמן כנקלטה
                      </Button>
                      <Button
                        size="xs"
                        color="green"
                        radius="md"
                        data-testid={`printers-settings-job-printed-${job.id}`}
                        onClick={() => onUpdatePrintJobStatus(job.id, 'printed')}
                        disabled={isUpdatingPrintJob || job.status === 'printed'}
                      >
                        סמן כהודפסה
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        radius="md"
                        data-testid={`printers-settings-job-failed-${job.id}`}
                        onClick={() => onUpdatePrintJobStatus(job.id, 'failed')}
                        disabled={isUpdatingPrintJob || job.status === 'failed'}
                      >
                        סמן ככשלון
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {printJobs.length === 0 && !isLoadingPrintJobs && (
              <tr>
                <td colSpan={5} className={settingsClasses.empty}>אין עבודות הדפסה עדיין.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const PosPrintersSettingsContent = React.memo(PosPrintersSettingsContentComponent);
